/**
 * Billy's Speed Shop — Vehicle Selection Engine v2
 * Manages vehicle storage, dropdown cascading, and fitment matching.
 * Exposes window.BillyVehicle as the public API.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'billy_vehicle_v2';
  var DATA_URL_ATTR = 'data-bvs-data-url';
  var INDEX_URL_ATTR = 'data-bvs-index-url';
  var STALE_DAYS = 14;

  var _data = null;
  var _dataPromise = null;
  var _index = null;
  var _indexPromise = null;

  /* ═══════════════════════════════════════════════════
     localStorage — get / set / clear
     ═══════════════════════════════════════════════════ */

  function get() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function set(vehicle) {
    if (!vehicle || !vehicle.make) return;
    var clean = {
      make: vehicle.make,
      model: vehicle.model || null,
      submodel: vehicle.submodel || null,
      year: vehicle.year ? String(vehicle.year) : null,
      engine: vehicle.engine || null
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(clean));
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        console.warn('[BillyVehicle] Storage quota exceeded — vehicle may not persist.');
        document.dispatchEvent(new CustomEvent('billy:storage-full'));
      }
    }
    document.dispatchEvent(new CustomEvent('billy:vehicle-changed', { detail: clean }));
  }

  function clear() {
    try { localStorage.removeItem(STORAGE_KEY); }
    catch (e) { /* silent */ }
    document.dispatchEvent(new CustomEvent('billy:vehicle-cleared'));
  }

  /* ═══════════════════════════════════════════════════
     Data loading
     ═══════════════════════════════════════════════════ */

  function getAttr(attr) {
    var el = document.querySelector('[' + attr + ']');
    return el ? el.getAttribute(attr) : null;
  }

  function checkStale(json) {
    if (!json || !json.generated) return;
    var age = Date.now() - new Date(json.generated).getTime();
    var days = Math.floor(age / (24 * 60 * 60 * 1000));
    if (days > STALE_DAYS) {
      console.warn('[BillyVehicle] Data is ' + days + ' days old — consider regenerating from PIM.');
    }
  }

  function loadData() {
    if (_data) return Promise.resolve(_data);
    if (_dataPromise && _dataPromise.then) return _dataPromise;
    var url = getAttr(DATA_URL_ATTR);
    if (!url) return Promise.resolve(null);
    _dataPromise = fetch(url)
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (json) { checkStale(json); _data = json; return _data; })
      .catch(function (err) { console.warn('[BillyVehicle] Cascade data failed:', err.message); _data = null; _dataPromise = null; return null; });
    return _dataPromise;
  }

  function loadIndex() {
    if (_index) return Promise.resolve(_index);
    if (_indexPromise && _indexPromise.then) return _indexPromise;
    var url = getAttr(INDEX_URL_ATTR);
    if (!url) return Promise.resolve(null);
    _indexPromise = fetch(url)
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (json) { checkStale(json); _index = json; return _index; })
      .catch(function (err) { console.warn('[BillyVehicle] Vehicle index failed:', err.message); _index = null; _indexPromise = null; return null; });
    return _indexPromise;
  }

  function clearCache(what) {
    if (!what || what === 'data' || what === 'all') { _data = null; _dataPromise = null; }
    if (!what || what === 'index' || what === 'all') { _index = null; _indexPromise = null; }
  }

  /* ═══════════════════════════════════════════════════
     Fitment matching — dictionary-encoded grouped index
     Row: [makeIdx, modelIdx, yearFrom, yearTo, [handleIdxs...], subIdx?, engineIdx?]
     ═══════════════════════════════════════════════════ */

  function findMatches(make, model, year, submodel, engine) {
    if (!_index || !_index.f) return {};
    var d = _index.d;
    var yearNum = year ? parseInt(year, 10) : null;
    var makeIdx = d.makes.indexOf(make);
    if (makeIdx === -1) return {};
    var modelIdx = model ? d.models.indexOf(model) : -1;
    var results = {};

    for (var i = 0; i < _index.f.length; i++) {
      var row = _index.f[i];
      if (row[0] !== makeIdx) continue;
      if (modelIdx !== -1 && row[1] !== modelIdx) continue;
      if (yearNum && (yearNum < row[2] || yearNum > row[3])) continue;
      if (submodel && row.length > 5 && row[5] >= 0) {
        var rowSub = d.subs[row[5]] || '';
        if (rowSub && rowSub !== submodel) continue;
      }
      if (engine && row.length > 6 && row[6] >= 0) {
        var rowEng = d.engines[row[6]] || '';
        if (rowEng && rowEng !== engine) continue;
      }
      var handleIdxs = row[4];
      for (var h = 0; h < handleIdxs.length; h++) {
        var handle = d.handles[handleIdxs[h]];
        if (handle) results[handle] = true;
      }
    }
    return results;
  }

  function findMatchesForStored() {
    var v = get();
    if (!v || !v.make) return null;
    return findMatches(v.make, v.model, v.year, v.submodel, v.engine);
  }

  function getMatchingProducts(matchHandles) {
    if (!_index || !_index.p) return [];
    var out = [];
    for (var handle in matchHandles) {
      if (!matchHandles.hasOwnProperty(handle)) continue;
      var p = _index.p[handle];
      if (p) out.push({ handle: handle, title: p.t, vendor: p.v, sku: p.s, price: p.p, partType: p.pt, shortDesc: p.sd, notes: p.n || [] });
    }
    return out;
  }

  function buildResultsUrl(vehicle) {
    var v = vehicle || get();
    if (!v || !v.make) return '/pages/vehicle-results';
    var params = new URLSearchParams();
    params.set('make', v.make);
    if (v.model) params.set('model', v.model);
    if (v.submodel) params.set('submodel', v.submodel);
    if (v.year) params.set('year', v.year);
    if (v.engine) params.set('engine', v.engine);
    return '/pages/vehicle-results?' + params.toString();
  }

  /* ═══════════════════════════════════════════════════
     Cascade dropdown helpers
     ═══════════════════════════════════════════════════ */

  function populateSelect(selectEl, options, placeholder) {
    selectEl.innerHTML = '';
    var ph = document.createElement('option');
    ph.value = ''; ph.textContent = placeholder || 'Select...';
    selectEl.appendChild(ph);
    for (var i = 0; i < options.length; i++) {
      var opt = document.createElement('option');
      opt.value = options[i]; opt.textContent = options[i];
      selectEl.appendChild(opt);
    }
    selectEl.disabled = options.length === 0;
  }

  function resetSelect(selectEl, placeholder) {
    selectEl.innerHTML = '';
    var ph = document.createElement('option');
    ph.value = ''; ph.textContent = placeholder || 'Select...';
    selectEl.appendChild(ph);
    selectEl.disabled = true;
  }

  var cascade = {
    populateMakes: function (selectEl) {
      if (!_data || !_data.makes) { resetSelect(selectEl, 'Data unavailable'); return; }
      populateSelect(selectEl, _data.makes, 'Select Make...');
    },
    populateModels: function (selectEl, make) {
      if (!_data || !_data.models || !_data.models[make]) { resetSelect(selectEl, 'Select Make first'); return; }
      populateSelect(selectEl, _data.models[make], 'Select Model...');
    },
    populateSubmodels: function (selectEl, make, model) {
      var key = make + '|' + model;
      if (!_data || !_data.submodels || !_data.submodels[key]) return { available: false };
      var subs = _data.submodels[key].filter(function (s) { return s !== ''; });
      if (subs.length === 0) return { available: false };
      populateSelect(selectEl, subs, 'All Submodels');
      return { available: true };
    },
    populateYears: function (selectEl, make, model) {
      var key = make + '|' + model;
      if (!_data || !_data.years || !_data.years[key]) { resetSelect(selectEl, 'Select Model first'); return; }
      var years = _data.years[key].slice().sort(function (a, b) { return b - a; });
      populateSelect(selectEl, years.map(String), 'Select Year...');
    },
    populateEngines: function (selectEl, make, model, year) {
      var key = make + '|' + model + '|' + year;
      if (!_data || !_data.engines || !_data.engines[key]) return { available: false, autoSelected: null };
      var engines = _data.engines[key];
      if (engines.length === 1) {
        populateSelect(selectEl, engines, 'Select Engine...');
        selectEl.value = engines[0];
        return { available: true, autoSelected: engines[0] };
      }
      populateSelect(selectEl, engines, 'All Engines');
      return { available: true, autoSelected: null };
    }
  };

  /* ═══════════════════════════════════════════════════
     Display helper
     ═══════════════════════════════════════════════════ */

  function formatVehicle(vehicle) {
    var v = vehicle || get();
    if (!v || !v.make) return '';
    var parts = [];
    if (v.year) parts.push(v.year);
    parts.push(v.make);
    if (v.model) parts.push(v.model);
    if (v.submodel) parts.push(v.submodel);
    if (v.engine) parts.push('\u00B7 ' + v.engine);
    return parts.join(' ');
  }

  /* ═══════════════════════════════════════════════════
     Public API
     ═══════════════════════════════════════════════════ */

  window.BillyVehicle = {
    get: get,
    set: set,
    clear: clear,
    loadData: loadData,
    loadIndex: loadIndex,
    clearCache: clearCache,
    cascade: cascade,
    findMatches: findMatches,
    findMatchesForStored: findMatchesForStored,
    getMatchingProducts: getMatchingProducts,
    buildResultsUrl: buildResultsUrl,
    formatVehicle: formatVehicle
  };

})();
